/**
 * Security utilities for protecting user data
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

/**
 * Verifies that the provided ID matches the current authenticated user
 */
export const verifyOwnerId = async (userId?: string): Promise<boolean> => {
  if (!userId) return false;
  
  try {
    const { data } = await supabase.auth.getUser();
    return data?.user?.id === userId;
  } catch (error) {
    logger.error("Error verifying user ownership:", error);
    return false;
  }
};

/**
 * Validates that an array of data objects all belong to the current user
 */
export const filterOwnedData = async <T extends Record<string, any>>(
  data: T[],
  userIdField: string = 'user_id'
): Promise<T[]> => {
  if (!data || !Array.isArray(data)) return [];
  
  try {
    const { data: authData } = await supabase.auth.getUser();
    const currentUserId = authData?.user?.id;
    
    if (!currentUserId) return [];
    
    return data.filter(item => item[userIdField] === currentUserId);
  } catch (error) {
    logger.error("Error filtering owned data:", error);
    return [];
  }
};

export type UserRole = 'user' | 'advertiser' | 'admin';

/**
 * Checks if the current user has the required role using a safe approach
 * that avoids RLS recursion by using the security definer function
 */
export const checkUserRole = async (requiredRole: UserRole): Promise<boolean> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user?.id) {
      logger.debug("No authenticated user found");
      return false;
    }

    logger.debug("Checking role for user:", userData.user.id, "Required role:", requiredRole);

    const { data, error } = await supabase.rpc('has_role', {
      _user_id: userData.user.id,
      _role: requiredRole
    });
      
    if (error) {
      logger.error("Error checking user role via RPC:", error);
      
      if (error.message?.includes('infinite recursion')) {
        logger.error("RLS recursion detected in role check - this should not happen with security definer");
      }
      
      return false;
    }
    
    logger.debug("Role check result:", data);
    return !!data;
  } catch (error) {
    logger.error("Exception checking user role:", error);
    return false;
  }
};

/**
 * Enhanced role checker with retry logic for handling transient database issues
 */
export const checkUserRoleWithRetry = async (
  requiredRole: UserRole, 
  maxRetries: number = 2
): Promise<boolean> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await checkUserRole(requiredRole);
      return result;
    } catch (error: any) {
      logger.error(`Role check attempt ${attempt} failed:`, error);
      
      if (error.message?.includes('infinite recursion')) {
        logger.error("Infinite recursion detected - not retrying");
        return false;
      }
      
      if (attempt === maxRetries) {
        logger.error("All role check attempts failed");
        return false;
      }
      
      await new Promise(resolve => setTimeout(resolve, 500 * attempt));
    }
  }
  
  return false;
};

/**
 * Generates a simple CSRF token for client-side use
 */
export const generateCSRFToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Stores CSRF token in sessionStorage
 */
export const storeCSRFToken = (token: string): void => {
  try {
    sessionStorage.setItem('csrf_token', token);
  } catch (error) {
    logger.warn('Could not store CSRF token:', error);
  }
};

/**
 * Retrieves CSRF token from sessionStorage
 */
export const getCSRFToken = (): string | null => {
  try {
    return sessionStorage.getItem('csrf_token');
  } catch (error) {
    logger.warn('Could not retrieve CSRF token:', error);
    return null;
  }
};

/**
 * Initializes CSRF token if not present
 */
export const initializeCSRFToken = (): string => {
  let token = getCSRFToken();
  if (!token) {
    token = generateCSRFToken();
    storeCSRFToken(token);
  }
  return token;
};
